import { useCallback, useMemo, useState } from "react";
import BreadCrumb from "components/common/BreadCrumb";
import TableContainer from "components/common/TableContainer";
import { ColumnDef } from "@tanstack/react-table";
import { IndexColumn } from "components/common/columns";
import {
  downloadMyPayrollSlipPdf,
  getMyPayrollSlips,
} from "../services/PayrollService";
import type { PayrollSlip } from "../types/payroll.types";
import StatusBadge from "components/common/StatusBadge";
import { payrollRunStatusMap } from "../constants/payroll.constants";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Input,
  Label,
  Spinner,
} from "reactstrap";
import { formatCurrency } from "utils/currency";
import { getDocumentTitle } from "utils/helpers";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 5 }, (_, i) => currentYear - 2 + i);

function formatMonthYear(month: number, year: number): string {
  return new Date(year, month - 1, 1).toLocaleString("default", {
    month: "long",
    year: "numeric",
  });
}

async function triggerDownload(slip: PayrollSlip): Promise<void> {
  const blob = await downloadMyPayrollSlipPdf(slip.id);
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `payslip-${slip.id}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

const MyPayslips = () => {
  document.title = getDocumentTitle("My Payslips");
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [year, setYear] = useState<number | undefined>(
    new Date().getFullYear(),
  );
  const [downloading, setDownloading] = useState<number | null>(null);

  const handleDownload = useCallback(async (slip: PayrollSlip) => {
    setDownloading(slip.id);
    try {
      await triggerDownload(slip);
    } finally {
      setDownloading(null);
    }
  }, []);

  const fetchApi = useCallback(
    async ({ page, limit }: { page: number; limit: number }) => {
      const res = await getMyPayrollSlips({ page, limit, month, year });
      return {
        success: res.success,
        message: res.message,
        data: res.data ?? {
          rows: [] as PayrollSlip[],
          totalItems: 0,
          currentPage: page,
          pageSize: limit,
          limit,
          totalPages: 0,
        },
      };
    },
    [month, year],
  );

  const columns = useMemo<ColumnDef<PayrollSlip>[]>(
    () => [
      IndexColumn<PayrollSlip>(),
      {
        header: "Period",
        id: "period",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const { month, year } = row.original;
          return <span>{formatMonthYear(month, year)}</span>;
        },
      },
      {
        header: "Payroll Run",
        id: "payrollRun",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const run = row.original.payrollItem?.payrollRun;
          if (!run) return <span className="text-muted">—</span>;
          return (
            <span className="fw-medium">{run.code ?? `#${run.id}`}</span>
          );
        },
      },
      {
        header: "Gross",
        id: "gross",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const v = row.original.payrollItem?.grossAmount;
          return <span>{formatCurrency(v)}</span>;
        },
      },
      {
        header: "Net Pay",
        id: "net",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const v = row.original.payrollItem?.netAmount;
          return (
            <span className="fw-medium text-success">{formatCurrency(v)}</span>
          );
        },
      },
      {
        header: "Status",
        id: "status",
        enableColumnFilter: false,
        cell: ({ row }) => {
          const s = row.original.payrollItem?.status;
          if (!s) return <span className="text-muted">—</span>;
          return (
            <StatusBadge value={s} map={payrollRunStatusMap} />
          );
        },
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        enableColumnFilter: false,
        cell: ({ row }) => {
          const slip = row.original;
          const busy = downloading === slip.id;
          return (
            <Button
              size="sm"
              color="soft-primary"
              disabled={busy}
              onClick={() => handleDownload(slip)}
              title="Download payslip PDF"
            >
              {busy ? (
                <Spinner size="sm" />
              ) : (
                <i className="ri-download-2-line align-middle" />
              )}
            </Button>
          );
        },
      },
    ],
    [downloading, handleDownload],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title="My Payslips" pageTitle="Payroll" />
        <Card>
          <CardHeader>
            <h5 className="card-title mb-0">My Payslips</h5>
          </CardHeader>
          <CardBody>
            <div className="d-flex align-items-end justify-content-end gap-3 mb-4">
              <div>
                <Label className="form-label mb-1">Month</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  style={{ width: 140 }}
                  value={month ?? ""}
                  onChange={(e) =>
                    setMonth(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                >
                  <option value="">All months</option>
                  {MONTHS.map((name, i) => (
                    <option key={i + 1} value={i + 1}>
                      {name}
                    </option>
                  ))}
                </Input>
              </div>
              <div>
                <Label className="form-label mb-1">Year</Label>
                <Input
                  type="select"
                  bsSize="sm"
                  style={{ width: 110 }}
                  value={year ?? ""}
                  onChange={(e) =>
                    setYear(
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                >
                  <option value="">All years</option>
                  {YEAR_OPTIONS.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </Input>
              </div>
            </div>
            <TableContainer<PayrollSlip>
              columns={columns}
              fetchApi={fetchApi}
              pageSize={10}
              globalSearch={false}
              divClass="table-responsive table-card mb-3"
              tableClass="align-middle table-nowrap mb-0"
              theadClass="table-light text-muted"
            />
          </CardBody>
        </Card>
      </Container>
    </div>
  );
};

export default MyPayslips;